import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Music, Pause, Play, Volume2 } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import { WHATSAPP_CTA_URL } from '../../../data/whatsapp';

type CardData = typeof cards[number];
type NavigatorWithConnection = Navigator & {
  connection?: {
    effectiveType?: string;
  };
};
type ProgressSlide = HTMLElement & {
  progress?: number;
};

function isSlowConnection() {
  const effectiveType = (navigator as NavigatorWithConnection).connection?.effectiveType;
  return effectiveType === 'slow-2g' || effectiveType === '2g' || effectiveType === '3g';
}

function syncSlidePresence(swiper: SwiperType) {
  swiper.slides.forEach((slide) => {
    const progress = Math.abs((slide as ProgressSlide).progress ?? 0);
    const isHidden = progress >= 2.45;
    const isCenter = progress < 0.5;
    const isNear = progress >= 0.5 && progress < 1.5;

    slide.style.opacity = isHidden ? '0' : isCenter ? '1' : isNear ? '0.78' : '0.46';
    slide.style.pointerEvents = isHidden ? 'none' : '';
    slide.style.zIndex = isHidden ? '0' : isCenter ? '10' : isNear ? '5' : '3';
  });
}

/* ─── Cards data ─────────────────────────────────────────────────── */
const cards = [
  {
    id: 'casamento',
    genre: 'Sertanejo',
    genreHue: '#0d9488',
    occasion: 'Casamento',
    quote: '"Os convidados acharam incrível!"',
    videoUrl: 'https://ula8sbsflkoyql15.public.blob.vercel-storage.com/videos-site/casamento.mp4',
    poster: 'https://ula8sbsflkoyql15.public.blob.vercel-storage.com/videos-site/casamento-imagem.jpg',
  },
  {
    id: 'gospel-mae',
    genre: 'Gospel',
    genreHue: '#6d28d9',
    occasion: 'Aniversário da mãe',
    quote: '"Nunca vimos minha mãe tão emocionada!"',
    videoUrl: 'https://ula8sbsflkoyql15.public.blob.vercel-storage.com/videos-site/maria-gospel.mp4',
    poster: 'https://ula8sbsflkoyql15.public.blob.vercel-storage.com/videos-site/maria-gospel-imagem.jpg',
  },
  {
    id: 'formatura',
    genre: 'Pop',
    genreHue: '#e11d48',
    occasion: 'Formatura',
    quote: '"A galera pirou na música!"',
    videoUrl: 'https://ula8sbsflkoyql15.public.blob.vercel-storage.com/videos-site/formatura.mp4',
    poster: 'https://ula8sbsflkoyql15.public.blob.vercel-storage.com/videos-site/formatura-camila-imagem.jpg',
  },
  {
    id: 'sertanejo-pai',
    genre: 'Sertanejo',
    genreHue: '#dc2626',
    occasion: 'Presente para o pai',
    quote: '"Meu pai é quieto, mas quando ouviu chorou"',
    videoUrl: 'https://ula8sbsflkoyql15.public.blob.vercel-storage.com/videos-site/sertanejo-pai.mp4',
    poster: 'https://ula8sbsflkoyql15.public.blob.vercel-storage.com/videos-site/sertanejo-pai-imagem.jpg',
  },
  {
    id: 'bodas',
    genre: 'Bossa Nova',
    genreHue: '#0369a1',
    occasion: 'Bodas',
    quote: '"50 anos que viraram música"',
    videoUrl: 'https://ula8sbsflkoyql15.public.blob.vercel-storage.com/videos-site/helena-roberto-bodas.mp4',
    poster: 'https://ula8sbsflkoyql15.public.blob.vercel-storage.com/videos-site/helena-roberto-bodas-imagem.jpg',
  },
  {
    id: 'musicainfantil',
    genre: 'Música Infantil',
    genreHue: '#b45309',
    occasion: 'Aniversário do filho',
    quote: '"Nosso filho amou!"',
    videoUrl: 'https://ula8sbsflkoyql15.public.blob.vercel-storage.com/videos-site/festa-infantil.mp4',
    poster: 'https://ula8sbsflkoyql15.public.blob.vercel-storage.com/videos-site/festa-infantil-imagem.jpg',
  },
  {
    id: 'gospel-avo',
    genre: 'Gospel',
    genreHue: '#ca8a04',
    occasion: 'Aniversário da avó',
    quote: '"Minha avó se emocionou muito com a música em seu aniversário de 90 anos."',
    videoUrl: 'https://ula8sbsflkoyql15.public.blob.vercel-storage.com/videos-site/gospel-avo.mp4',
    poster: 'https://ula8sbsflkoyql15.public.blob.vercel-storage.com/videos-site/gospel-avo-imagem.jpg',
  },
];

/* ─── Video card ─────────────────────────────────────────────────── */
function VideoCard({
  card,
  isActive,
}: {
  card: CardData;
  isActive: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSoundOn, setIsSoundOn] = useState(false);
  const [showButton, setShowButton] = useState(true);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;

    if (isActive) {
      if (isSlowConnection()) {
        video.preload = 'none';
        video.muted = true;
        setIsPlaying(false);
        setIsSoundOn(false);
        return () => { cancelled = true; };
      }

      video.preload = 'metadata';
      video.muted = true;
      setIsSoundOn(false);
      video
        .play()
        .then(() => { if (!cancelled) setIsPlaying(true); })
        .catch(() => { if (!cancelled) setIsPlaying(false); });
    } else {
      video.pause();
      video.preload = 'none';
      video.muted = true;
      setIsPlaying(false);
      setIsSoundOn(false);
      setShowButton(true);
    }

    // NOTE: never clear video.src here — the old cleanup runs before the new effect,
    // which would blank the src before play() fires on newly-active cards.
    return () => { cancelled = true; };
  }, [isActive]);

  // Auto-hide button 1.5s after playback starts
  useEffect(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (isPlaying) {
      hideTimerRef.current = setTimeout(() => setShowButton(false), 1500);
    } else {
      setShowButton(true);
    }
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, [isPlaying]);

  // Reveal button for autoHideMs, then re-hide if still playing
  const revealButton = (autoHideMs = 2500) => {
    setShowButton(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (isPlaying) {
      hideTimerRef.current = setTimeout(() => setShowButton(false), autoHideMs);
    }
  };

  const toggleSound = () => {
    const video = videoRef.current;
    if (!video || !isActive) return;

    if (video.paused) {
      video.preload = 'auto';
      video.muted = false;
      video
        .play()
        .then(() => {
          setIsPlaying(true);
          setIsSoundOn(true);
        })
        .catch(() => {
          video.muted = true;
          setIsPlaying(false);
          setIsSoundOn(false);
        });
    } else {
      const nextSoundOn = !isSoundOn;
      video.muted = !nextSoundOn;
      setIsSoundOn(nextSoundOn);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    toggleSound();
  };

  const showSoundIcon = isActive && isPlaying && isSoundOn;
  const showPauseIcon = isActive && isPlaying && !isSoundOn;
  const buttonIsActive = showSoundIcon || showPauseIcon;
  const iconColor = buttonIsActive ? '#fff' : 'var(--gold)';
  const buttonVisible = isActive && showButton;

  // Desktop hover: reveal immediately, hide 800ms after leaving
  const handleMouseEnter = () => revealButton(2500);
  const handleMouseLeave = () => {
    if (isPlaying) {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => setShowButton(false), 800);
    }
  };

  // Click/tap: first tap reveals controls, second tap acts
  const handleClick = () => {
    if (!isActive) return;
    if (!showButton) { revealButton(2500); return; }
    toggleSound();
  };

  return (
    <div
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="button"
      tabIndex={isActive ? 0 : -1}
      aria-label={isSoundOn ? `Silenciar amostra de ${card.genre}` : `Ouvir amostra de ${card.genre}`}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        cursor: 'pointer',
        borderRadius: 20,
        overflow: 'hidden',
      }}
    >
      <video
        ref={videoRef}
        src={card.videoUrl}
        poster={card.poster}
        preload="none"
        loop
        playsInline
        muted
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />

      {/* Gradient overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.18) 45%, transparent 100%)',
        }}
      />

      {/* Genre badge */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          background: card.genreHue,
          color: '#fff',
          borderRadius: 999,
          padding: '3px 10px',
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.06em',
          fontFamily: "'DM Sans', sans-serif",
          textTransform: 'uppercase',
        }}
      >
        {card.genre}
      </div>

      {/* Play / Pause / Volume button — fades out 1.5s after play starts, returns on hover */}
      {isActive && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: buttonIsActive ? 'var(--gold)' : 'rgba(255,255,255,0.88)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
            opacity: buttonVisible ? 1 : 0,
            pointerEvents: buttonVisible ? 'auto' : 'none',
            transition: 'background 0.2s, opacity 0.4s ease',
          }}
        >
          {showSoundIcon ? (
            <Volume2 size={20} color={iconColor} strokeWidth={2.4} />
          ) : showPauseIcon ? (
            <Pause size={18} color={iconColor} fill={iconColor} strokeWidth={2.4} />
          ) : (
            <Play size={17} color={iconColor} fill={iconColor} strokeWidth={2.4} />
          )}
        </div>
      )}

      {/* Bottom text */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px 14px' }}>
        <p
          style={{
            color: 'rgba(255,255,255,0.65)',
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            margin: '0 0 4px',
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          {card.occasion}
        </p>
        <p
          style={{
            color: '#fff',
            fontSize: 11,
            lineHeight: 1.4,
            margin: 0,
            fontFamily: "'DM Sans', sans-serif",
            fontStyle: 'italic',
          }}
        >
          {card.quote}
        </p>
      </div>

      {/* Active border ring */}
      {isActive && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 20,
            boxShadow: 'inset 0 0 0 2.5px rgba(255,145,77,0.70)',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
}

/* ─── Blob helper ─────────────────────────────────────────────────── */
function Blob({ style }: { style: React.CSSProperties }) {
  return <div style={{ position: 'absolute', borderRadius: '50%', pointerEvents: 'none', ...style }} />;
}

/* ─── Main Hero ───────────────────────────────────────────────────── */
export default function Hero() {
  const [mounted, setMounted] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);

  useEffect(() => { const t = setTimeout(() => setMounted(true), 60); return () => clearTimeout(t); }, []);

  return (
    <section style={{ position: 'relative', overflow: 'clip', background: 'var(--cream)', marginTop: -96, paddingTop: 96 }}>

      {/* Blobs */}
      <Blob style={{ top: '-180px', right: '-60px', width: 560, height: 560, background: 'radial-gradient(ellipse at 40% 40%, rgba(255,145,77,0.18) 0%, rgba(255,145,77,0.07) 45%, transparent 70%)', filter: 'blur(50px)' }} />
      <Blob style={{ top: '20%', left: '-120px', width: 420, height: 420, background: 'radial-gradient(ellipse at 60% 50%, rgba(255,145,77,0.13) 0%, rgba(255,176,122,0.05) 50%, transparent 70%)', filter: 'blur(62px)' }} />
      <Blob style={{ bottom: '-60px', left: '28%', width: 580, height: 280, background: 'radial-gradient(ellipse at 50% 30%, rgba(255,145,77,0.10) 0%, transparent 65%)', filter: 'blur(70px)' }} />

      {/* Content */}
      <div style={{
        position: 'relative', zIndex: 10,
        maxWidth: 1180, margin: '0 auto',
        padding: '28px 24px 48px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 32,
      }}>

        {/* Headline */}
        <div style={{
          textAlign: 'center', maxWidth: 680,
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(28px)',
          transition: 'opacity 0.65s ease, transform 0.65s ease',
        }}>
          <h1 style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 'clamp(36px, 6vw, 64px)',
            fontWeight: 800, lineHeight: 1.08,
            letterSpacing: '-0.03em',
            color: 'var(--heading-ink)',
            margin: '0 0 28px',
          }}>
            Uma música{' '}
            <span style={{ color: 'var(--gold)', position: 'relative', display: 'inline-block' }}>
              única
              <svg viewBox="0 0 120 10" style={{ position: 'absolute', bottom: -4, left: 0, width: '100%', height: 8, overflow: 'visible' }} preserveAspectRatio="none">
                <path d="M2,6 Q30,1 60,5 Q90,9 118,4" stroke="var(--gold)" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.5" />
              </svg>
            </span>
            , feita especialmente<br />para a sua ocasião.
          </h1>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href={WHATSAPP_CTA_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--gold)', color: '#fff', textDecoration: 'none', borderRadius: 999, padding: '14px 32px', fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700, boxShadow: '0 4px 24px rgba(255,145,77,0.40)', transition: 'background 0.2s, box-shadow 0.2s, transform 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--gold-hover)'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(255,145,77,0.50)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--gold)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(255,145,77,0.40)'; }}
            >
              <Music size={18} strokeWidth={2.5} />
              Criar minha música
            </a>
            <button
              onClick={() => { const el = document.getElementById('como-funciona'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1.5px solid var(--border-soft)', color: 'var(--text-strong)', borderRadius: 999, padding: '13px 28px', fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 500, cursor: 'pointer', transition: 'border-color 0.2s, color 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.color = 'var(--gold)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-soft)'; e.currentTarget.style.color = 'var(--text-strong)'; }}
            >
              Como funciona?
            </button>
          </div>
        </div>

        {/* Cards carousel */}
        <div
          style={{
            width: '100%',
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.75s ease 0.15s, transform 0.75s ease 0.15s',
          }}
        >
          <p
            style={{
              textAlign: 'center',
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--text-soft)',
              marginBottom: 16,
              letterSpacing: '0.04em',
            }}
          >
            ▶ Clique em qualquer card para ouvir uma amostra
          </p>

          <div
            className="hero-video-coverflow"
            style={{ position: 'relative', width: '100%', maxWidth: 840, margin: '0 auto' }}
          >
            {/* Seta — anterior */}
            <button
              onClick={() => swiperRef.current?.slidePrev()}
              aria-label="Slide anterior"
              style={{
                position: 'absolute',
                top: '50%',
                left: 8,
                transform: 'translateY(-50%)',
                zIndex: 20,
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.90)',
                border: 'none',
                boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <ChevronLeft size={17} color="var(--text-strong)" strokeWidth={2.5} />
            </button>

            <Swiper
              onSwiper={(s) => {
                swiperRef.current = s;
                syncSlidePresence(s);
              }}
              effect="coverflow"
              grabCursor
              centeredSlides
              slidesPerView="auto"
              loop
              slideToClickedSlide
              coverflowEffect={{
                rotate: 0,
                stretch: 80,
                depth: 0,
                scale: 0.84,
                modifier: 1,
                slideShadows: false,
              }}
              modules={[EffectCoverflow]}
              onProgress={syncSlidePresence}
              onSetTranslate={syncSlidePresence}
              onSlideChange={(s) => {
                setActiveIndex(s.realIndex);
                syncSlidePresence(s);
              }}
              style={{ padding: '12px 0 20px', overflow: 'visible', maxWidth: 760, margin: '0 auto' }}
            >
              {cards.map((card, i) => (
                <SwiperSlide
                  key={card.id}
                  style={{
                    width: 'clamp(180px, 58vw, 220px)',
                    aspectRatio: '9 / 16',
                    borderRadius: 20,
                    overflow: 'hidden',
                  }}
                >
                  <VideoCard card={card} isActive={activeIndex === i} />
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Seta — próximo */}
            <button
              onClick={() => swiperRef.current?.slideNext()}
              aria-label="Próximo slide"
              style={{
                position: 'absolute',
                top: '50%',
                right: 8,
                transform: 'translateY(-50%)',
                zIndex: 20,
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.90)',
                border: 'none',
                boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <ChevronRight size={17} color="var(--text-strong)" strokeWidth={2.5} />
            </button>
          </div>

          {/* Indicador de reprodução */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'rgba(255,145,77,0.10)',
                border: '1px solid rgba(255,145,77,0.30)',
                borderRadius: 999,
                padding: '4px 14px',
                fontSize: 10,
                fontWeight: 600,
                color: 'var(--gold)',
                fontFamily: "'DM Sans', sans-serif",
                letterSpacing: '0.04em',
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: 'var(--gold)',
                  animation: 'pulse-dot 1.2s ease-in-out infinite',
                  flexShrink: 0,
                }}
              />
              {cards[activeIndex]?.genre} — clique para ouvir com som
            </div>
          </div>
        </div>

        {/* Genre strip */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center',
          opacity: mounted ? 1 : 0,
          transition: 'opacity 0.65s ease 0.30s',
        }}>
          {['Sertanejo', 'MPB', 'Gospel', 'Forró', 'Pagode', 'Pop', 'Funk', 'Rock', 'Axé', 'Bossa Nova', 'Reggae', '+3 estilos'].map((genre) => (
            <span key={genre} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', background: 'var(--warm-white)', border: '1px solid var(--border-soft)', borderRadius: 999, padding: '5px 14px' }}>
              {genre}
            </span>
          ))}
        </div>

      </div>
    </section>
  );
}
